import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Star, Quote } from 'lucide-react';
import { useTestimonials } from '../hooks/useAPI';

const TestimonialsSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { data: testimonials, isLoading, error } = useTestimonials();

  // Default testimonials if API fails
  const defaultTestimonials = [
    {
      id: 1,
      name: 'Sarah Johnson',
      role: 'CEO',
      company: 'TechStart Inc.',
      content: 'LionTek transformed our business operations with their innovative ERP solution. The efficiency gains have been remarkable.',
      rating: 5,
      image: null
    },
    {
      id: 2,
      name: 'Michael Chen',
      role: 'CTO',
      company: 'DataFlow Systems',
      content: 'Their AI/ML expertise helped us implement predictive analytics that increased our operational efficiency by 40%.',
      rating: 5,
      image: null
    },
    {
      id: 3,
      name: 'Emily Rodriguez',
      role: 'Operations Director',
      company: 'Global Manufacturing Co.',
      content: 'The cybersecurity audit revealed critical vulnerabilities and their team provided comprehensive solutions.',
      rating: 5,
      image: null
    },
    {
      id: 4,
      name: 'David Thompson',
      role: 'Marketing Manager',
      company: 'Growth Dynamics',
      content: 'LionTek\'s digital marketing strategies delivered exceptional ROI and helped us reach new market segments.',
      rating: 5,
      image: null
    },
    {
      id: 5,
      name: 'Lisa Wang',
      role: 'HR Director',
      company: 'People First Corp',
      content: 'Their corporate training programs upskilled our entire development team on modern technologies.',
      rating: 5,
      image: null
    },
    {
      id: 6,
      name: 'James Wilson',
      role: 'Innovation Lead',
      company: 'Future Tech Labs',
      content: 'The R&D collaboration resulted in breakthrough solutions that gave us a significant competitive advantage.',
      rating: 5,
      image: null
    }
  ];

  const displayTestimonials = testimonials && testimonials.length > 0 ? testimonials : defaultTestimonials;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < rating ? 'text-lion-yellow fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <section id="testimonials" className="py-20 md:py-24 bg-white">
      <div className="container mx-auto px-6" ref={ref}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.span 
            variants={itemVariants}
            className="font-bold text-lion-yellow text-sm uppercase tracking-wider"
          >
            TESTIMONIALS
          </motion.span>
          
          <motion.h2 
            variants={itemVariants}
            className="text-3xl md:text-4xl font-poppins font-bold text-lion-blue mt-2 mb-4"
          >
            What Our Clients Say
          </motion.h2>
          
          <motion.p 
            variants={itemVariants}
            className="max-w-2xl mx-auto text-gray-600"
          >
            Don't just take our word for it. Here's what our valued clients have to say about working with LionTek.
          </motion.p>
        </motion.div>

        {isLoading ? (
          <div className="flex justify-center">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {displayTestimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id || index}
                variants={cardVariants}
                custom={index}
                className="bg-lion-gray p-6 rounded-lg relative hover:shadow-lg transition-all duration-300 group"
              >
                {/* Quote Icon */}
                <div className="absolute top-4 right-4">
                  <Quote className="w-6 h-6 text-lion-yellow opacity-20 group-hover:opacity-40 transition-opacity duration-300" />
                </div>

                {/* Rating */}
                <div className="flex items-center mb-4">
                  {renderStars(testimonial.rating)}
                </div>

                {/* Testimonial Content */}
                <p className="text-gray-700 mb-6 leading-relaxed italic">
                  "{testimonial.testimonial || testimonial.content}"
                </p>

                {/* Client Info */}
                <div className="flex items-center">
                  {/* Avatar */}
                  <div className="w-12 h-12 bg-gradient-to-br from-lion-blue to-lion-yellow rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {testimonial.clientImage ? (
                      <img
                        src={testimonial.clientImage}
                        alt={testimonial.clientName || testimonial.name || 'Client'}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      (testimonial.clientName || testimonial.name || 'C').charAt(0)
                    )}
                  </div>

                  {/* Client Details */}
                  <div>
                    <h4 className="font-poppins font-semibold text-lion-blue">
                      {testimonial.clientName || testimonial.name}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {testimonial.clientPosition || testimonial.role}
                    </p>
                    <p className="text-xs text-gray-500">
                      {testimonial.clientCompany || testimonial.company}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {error && (
          <motion.p 
            variants={itemVariants}
            className="text-gray-500 text-center mt-8"
          >
            Testimonials will be loaded shortly. Please check back soon.
          </motion.p>
        )}

        {/* Call to Action */}
        <motion.div
          variants={itemVariants}
          className="text-center mt-16"
        >
          <p className="text-gray-600 mb-6">
            Ready to join our satisfied clients?
          </p>
          <a
            href="#contact"
            className="inline-flex items-center bg-lion-yellow text-lion-blue px-8 py-3 rounded-lg font-semibold hover:bg-yellow-400 transform hover:scale-105 transition-all duration-300"
          >
            Start Your Project
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;